import type {
  BaseSyntheticEvent,
  FocusEventHandler,
  ReactElement
} from 'react';
import { cloneElement, useMemo, useState } from 'react';
import { isOnlyCallingCodeEntered } from '@elfsight/lib-utils';
import { type FieldValue, useFormDispatch, useFormSelector } from '../contexts';
import { validateRequiredFieldValue } from '../fields';

export interface FormFieldContainerProps {
  children: ReactElement;
  onChange?: (value?: FieldValue) => void;
  validator?: (index: string) => boolean | undefined;
  id: string;
}

export function FormFieldContainer({
  children,
  validator,
  onChange,
  id
}: FormFieldContainerProps) {
  const [invalidError, setInvalidError] = useState<boolean | undefined>();

  const dispatch = useFormDispatch();
  const { status, fields, started } = useFormSelector();
  const field = fields[id] || {};
  const { value, type, valid = true } = field;

  const handleChange = (
    nextValue?: string,
    event?: BaseSyntheticEvent<InputEvent>
  ) => {
    const processedNextValue =
      field.type === 'phone' && nextValue && isOnlyCallingCodeEntered(nextValue)
        ? ''
        : nextValue;
    const nextValid =
      processedNextValue && validator ? validator(processedNextValue) : true;

    if (
      event?.nativeEvent &&
      event.nativeEvent.inputType === 'insertFromPaste'
    ) {
      setInvalidError(!nextValid);
    } else {
      setInvalidError(false);
    }

    const newFieldValue = {
      id,
      value: processedNextValue,
      valid: nextValid
    };

    dispatch({
      type: 'changeField',
      field: type ? { ...newFieldValue, type } : newFieldValue
    });

    if (!started.value) {
      dispatch({
        type: 'setStarted',
        started: true
      });
    }

    onChange?.(processedNextValue);
  };

  const onBlur: FocusEventHandler = (e) => {
    setInvalidError(!valid);
    children?.props?.onBlur?.(e);
  };

  const requiredError =
    validateRequiredFieldValue(field) &&
    status &&
    ['failed', 'invalid'].includes(status);

  const derivedInvalidError = useMemo(
    () => invalidError ?? !valid,
    [invalidError, valid]
  );

  const highlightError = derivedInvalidError || requiredError;

  return cloneElement(children, {
    ...children.props,
    invalidError: derivedInvalidError,
    onChange: handleChange,
    requiredError,
    highlightError,
    onBlur,
    value,
    valid
  });
}
